import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { motion } from 'framer-motion';
import { Calculator, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { calculateRentRequestFinancials, calculateRentToIncomeRatio, checkTenantEligibility, formatCurrency } from '../../utils/financials';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
export function RentRequestCreate() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const [formData, setFormData] = useState({
    principal: 1200000,
    duration: 12,
    propertyAddress: '',
    monthlyIncome: 500000,
    employmentStatus: 'Permanent' as 'Permanent' | 'Contract' | 'Freelance'
  });
  const [showBreakdown, setShowBreakdown] = useState(false);
  const financials = calculateRentRequestFinancials(formData.principal, formData.duration);
  const rentToIncomeRatio = calculateRentToIncomeRatio(financials.monthlyInstallment, formData.monthlyIncome);
  const eligibility = checkTenantEligibility(formData.monthlyIncome, financials.monthlyInstallment, formData.employmentStatus, 35);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.propertyAddress.trim()) {
      toast.error('Please enter a property address');
      return;
    }
    if (!eligibility.eligible) {
      toast.error('You do not meet the eligibility criteria. Please adjust your rent amount or income.');
      return;
    }
    toast.success('Rent request submitted successfully!');
    setTimeout(() => navigate('/tenant/dashboard'), 1500);
  };
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors pb-16 lg:pb-0" data-id="element-4953">
      <Sidebar data-id="element-4954" />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300" data-id="element-4955">
        <DashboardHeader data-id="element-4956" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-4957">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-4958">
            <div className="mb-6 lg:mb-8" data-id="element-4959">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 truncate" data-id="element-4960">
                Create Rent Request
              </h1>
              <p className="text-gray-500 dark:text-gray-400 line-clamp-2" data-id="element-4961">
                Get your annual rent funded and pay monthly
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 lg:space-y-8" data-id="element-4962">
              {/* Property Details */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 transition-colors" data-id="element-4963">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 truncate" data-id="element-4964">
                  Property Details
                </h2>

                <div className="space-y-4" data-id="element-4965">
                  <div data-id="element-4966">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 truncate" data-id="element-4967">
                      Property Address *
                    </label>
                    <input type="text" required value={formData.propertyAddress} onChange={e => setFormData({
                    ...formData,
                    propertyAddress: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors" placeholder="e.g., 12 Admiralty Way, Lekki Phase 1, Lagos" data-id="element-4968" />
                  </div>

                  <div data-id="element-4969">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 truncate" data-id="element-4970">
                      Annual Rent Amount *
                    </label>
                    <div className="relative" data-id="element-4971">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" data-id="element-4972">
                        ₦
                      </span>
                      <input type="number" required min="100000" value={formData.principal} onChange={e => setFormData({
                      ...formData,
                      principal: Number(e.target.value)
                    })} className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors" placeholder="1,200,000" data-id="element-4973" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" data-id="element-4974">
                      Enter the total annual rent for the property
                    </p>
                  </div>

                  <div data-id="element-4975">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 truncate" data-id="element-4976">
                      Repayment Duration *
                    </label>
                    <select value={formData.duration} onChange={e => setFormData({
                    ...formData,
                    duration: Number(e.target.value)
                  })} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors" data-id="element-4977">
                      <option value={12} data-id="element-4978">12 Months</option>
                      <option value={6} data-id="element-4979">6 Months</option>
                      <option value={24} data-id="element-4980">24 Months</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Income Verification */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 transition-colors" data-id="element-4981">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 truncate" data-id="element-4982">
                  Income Verification
                </h2>

                <div className="space-y-4" data-id="element-4983">
                  <div data-id="element-4984">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 truncate" data-id="element-4985">
                      Monthly Income *
                    </label>
                    <div className="relative" data-id="element-4986">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" data-id="element-4987">
                        ₦
                      </span>
                      <input type="number" required min="50000" value={formData.monthlyIncome} onChange={e => setFormData({
                      ...formData,
                      monthlyIncome: Number(e.target.value)
                    })} className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors" placeholder="500,000" data-id="element-4988" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" data-id="element-4989">
                      Your verified monthly income (minimum ₦50,000)
                    </p>
                  </div>

                  <div data-id="element-4990">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 truncate" data-id="element-4991">
                      Employment Status *
                    </label>
                    <select value={formData.employmentStatus} onChange={e => setFormData({
                    ...formData,
                    employmentStatus: e.target.value as any
                  })} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors" data-id="element-4992">
                      <option value="Permanent" data-id="element-4993">Permanent Employment</option>
                      <option value="Contract" data-id="element-4994">Contract Employment</option>
                      <option value="Freelance" data-id="element-4995">
                        Freelance/Self-Employed (Not Eligible)
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Financial Breakdown */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border border-blue-100 dark:border-blue-800 p-4 sm:p-6 transition-colors" data-id="element-4996">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3" data-id="element-4997">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 truncate" data-id="element-4998">
                    <Calculator className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-4999" />
                    <span className="truncate" data-id="element-5000">Financial Breakdown</span>
                  </h2>
                  <button type="button" onClick={() => setShowBreakdown(!showBreakdown)} className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium whitespace-nowrap flex-shrink-0" data-id="element-5001">
                    {showBreakdown ? 'Hide' : 'Show'} Details
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-5002">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700 transition-colors" data-id="element-5003">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate" data-id="element-5004">
                      Monthly Payment
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white break-all" data-id="element-5005">
                      {formatCurrency(financials.monthlyInstallment)}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700 transition-colors" data-id="element-5006">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate" data-id="element-5007">
                      Total Repayment
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white break-all" data-id="element-5008">
                      {formatCurrency(financials.totalRepayment)}
                    </p>
                  </div>
                </div>

                {showBreakdown && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} className="space-y-3 pt-4 border-t border-blue-100 dark:border-blue-800" data-id="element-5009">
                    <div className="flex justify-between text-sm gap-4" data-id="element-5010">
                      <span className="text-gray-600 dark:text-gray-400 truncate" data-id="element-5011">
                        Principal (Annual Rent)
                      </span>
                      <span className="font-bold text-gray-900 dark:text-white whitespace-nowrap" data-id="element-5012">
                        {formatCurrency(financials.principal)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm gap-4" data-id="element-5013">
                      <span className="text-gray-600 dark:text-gray-400 truncate" data-id="element-5014">
                        Financier Interest (25% p.a.)
                      </span>
                      <span className="font-bold text-gray-900 dark:text-white whitespace-nowrap" data-id="element-5015">
                        {formatCurrency(financials.financierInterest)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm gap-4" data-id="element-5016">
                      <span className="text-gray-600 dark:text-gray-400 truncate" data-id="element-5017">
                        Renta Service Fee (25%)
                      </span>
                      <span className="font-bold text-gray-900 dark:text-white whitespace-nowrap" data-id="element-5018">
                        {formatCurrency(financials.rentaFee)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm pt-3 border-t border-blue-100 dark:border-blue-800 gap-4" data-id="element-5019">
                      <span className="font-bold text-gray-900 dark:text-white truncate" data-id="element-5020">
                        Total Repayment
                      </span>
                      <span className="font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap" data-id="element-5021">
                        {formatCurrency(financials.totalRepayment)}
                      </span>
                    </div>
                  </motion.div>}
              </div>

              {/* Eligibility Check */}
              <div className={`rounded-xl border-2 p-4 sm:p-6 transition-colors ${eligibility.eligible ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`} data-id="element-5022">
                <div className="flex items-start gap-3" data-id="element-5023">
                  {eligibility.eligible ? <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" data-id="element-5024" /> : <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" data-id="element-5025" />}
                  <div className="flex-1 min-w-0" data-id="element-5026">
                    <h3 className={`font-bold mb-2 truncate ${eligibility.eligible ? 'text-green-900 dark:text-green-300' : 'text-red-900 dark:text-red-300'}`} data-id="element-5027">
                      {eligibility.eligible ? '✓ Eligible for Funding' : '✗ Not Eligible'}
                    </h3>
                    <ul className="space-y-1" data-id="element-5028">
                      {eligibility.reasons.map((reason, idx) => <li key={idx} className={`text-sm break-words ${eligibility.eligible ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`} data-id="element-5029">
                          • {reason}
                        </li>)}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-5030">
                      <div className="flex items-center justify-between text-sm gap-4" data-id="element-5031">
                        <span className={eligibility.eligible ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'} data-id="element-5032">
                          Rent-to-Income Ratio
                        </span>
                        <span className={`font-bold whitespace-nowrap ${rentToIncomeRatio <= 40 ? 'text-green-900 dark:text-green-300' : 'text-red-900 dark:text-red-300'}`} data-id="element-5033">
                          {rentToIncomeRatio.toFixed(1)}%{' '}
                          {rentToIncomeRatio <= 40 ? '✓' : '✗'}
                          <span className="text-xs ml-1" data-id="element-5034">(Max 40%)</span>
                        </span>
                      </div>
                    </div>
                    {!eligibility.eligible && <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg" data-id="element-5035">
                        <p className="text-xs text-yellow-800 dark:text-yellow-300 break-words" data-id="element-5036">
                          <strong data-id="element-5037">Tip:</strong> Reduce your annual rent amount
                          or increase your monthly income to meet eligibility
                          criteria.
                        </p>
                      </div>}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-5038">
                <button type="button" onClick={() => navigate('/tenant/dashboard')} className="flex-1 py-4 border border-gray-300 dark:border-gray-600 rounded-xl font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" data-id="element-5039">
                  Cancel
                </button>
                <button type="submit" disabled={!eligibility.eligible || !formData.propertyAddress.trim()} className="flex-1 py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center shadow-lg shadow-blue-200 dark:shadow-blue-900/30" data-id="element-5040">
                  {!eligibility.eligible ? <>
                      <AlertCircle className="mr-2 h-5 w-5 flex-shrink-0" data-id="element-5041" />
                      Not Eligible
                    </> : <>
                      Submit Request
                      <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" data-id="element-5042" />
                    </>}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </main>
    </div>;
}